// pf/ticket-verify.ts — the check of a Dice round played on a pf ticket (pf-ticket-v1, SC-65843; design note §3.7, §5 R2).
//
// The SDK hands the game each ticket round's EVIDENCE (what the device held and persisted BEFORE the bet — the ticket and
// its commitment, the accepted terms, the attempt — and the server's reveal after it) and its own BINDING check (the
// revealed seed against the commitment it held, the ticket, the client seed, nonce 0). The binding's best verdict is
// "consistent": whether the roll and the paid amount are right is the game's math, so the evidence is run through the
// contract's own verifier (@blocklabsdev/rgs-contracts verifyPaidAmount, §8 steps 3-6). This module only maps the
// evidence onto that verifier's input and says what came out. It holds no math of its own.
//
// What it never does: call a round verified when the device did not hold its commitment before the bet (ordering
// evidence lost), when the proof is not out yet, or when the round was played under a math or a PF protocol this build
// does not pin (another contract: neither verified nor a mismatch — this build cannot judge it). The amount is verified
// only against the terms the device HELD before the bet (decision D5); without them only the outcome is.
//
// THE AMOUNT, from the player's side. The contract's verifier is asked for the OUTCOME (every outcome check runs) and for
// what the rules owe for it under the terms the device held — the paid amount, the ceiling applied — and the credited
// amount is then compared with that here: the same is verified; LESS is a mismatch (a bonus never lowers a payment, and
// the ceiling the player held is already applied); MORE is not verifiable — a bonus or a promotion adds money outside
// the published contract (the runtime prices it into the entitlement, and a bonus on a loss comes with no statement at
// all), so it is said as such, never "verified" and never a mismatch.
//
// THE SERVER'S SETTLEMENT STATEMENT (evidence.settlement) is checked, never used: nonce and line 0; a loss with no bonus
// is never priced; the denomination the device held; the rounding draw J and its exhaustion (an exhausted draw is the floor
// policy with no J — rgs-core-runtime settle_rounding.go), which a bonus does not change (it adds to the numerator, the
// denominator stays); and, with no bonus, the entitlement and the cap reduction.

import { verifyPaidAmount, type PfHeldTerms, type VerifyPaidAmountResult } from "@blocklabsdev/rgs-contracts";
import type { PfBindingResult, PfRoundEvidence } from "@blocklabsdev/rgs-game-manager-frontend-api";
import { DICE_V2_MATH_IDS, PINNED_PF_PROTOCOL_ID } from "../contract";

export type TicketCheck =
  /** The outcome is the one the held commitment and the client seed give; `amount` says whether the credited amount was
   *  verified too (against the held terms) or could not be (no terms held, a policy this build does not model, a bonus). */
  | {
      readonly kind: "verified";
      readonly amount: "verified" | "unverified" | "not_applicable";
      readonly result: VerifyPaidAmountResult;
      /** why the amount is unverified, when it is */
      readonly unverifiedBecause?: "no_terms" | "more_than_owed";
    }
  /** Something the server answered contradicts what the device held, or the recomputation: never fair. */
  | { readonly kind: "mismatch"; readonly failed: readonly string[] }
  /** No reveal yet (or the server states the proof is outstanding). */
  | { readonly kind: "proof_outstanding" }
  /** Consistent, but this device did not hold the commitment before the bet: the order is not provable from here. */
  | { readonly kind: "ordering_evidence_lost" }
  /** Played under a math or a PF protocol this build does not pin: it cannot judge it. */
  | { readonly kind: "other_contract"; readonly mathId: string; readonly pfProtocolId: string }
  /** The ticket was never bound (refused before any debit, or abandoned) and was revealed: its seed is the committed one
   *  (or not). There is no round to recompute. */
  | { readonly kind: "retired"; readonly consistent: boolean };

/** The terms the device held, as the contract's verifier reads them — or undefined when the amount cannot be verified
 *  with them: absent, or a settlement policy the v2 verifier does not model (it settles stochastically). */
export function heldTerms(evidence: PfRoundEvidence): PfHeldTerms | undefined {
  const t = evidence.acceptedTerms;
  if (!t || t.settlementPolicy !== "stochastic") return undefined;
  return {
    denomination: t.denominationMinor,
    ceiling: t.maxWinSemantics === "none" ? null : (t.maxWinMinor ?? null),
    ceilingEnforced: t.maxWinSemantics === "clamp",
  };
}

/** The stake in credits: the minor units over the denomination the device held (1 when it held no terms) — whether or not
 *  the amount itself can be verified. Throws when the stake is not a whole number of credits — no round the runtime
 *  accepts has one. */
function creditsOf(betMinor: number, held: PfRoundEvidence["acceptedTerms"]): bigint {
  const bet = BigInt(betMinor);
  const denomination = BigInt(held?.denominationMinor ?? "1");
  if (denomination <= 0n || bet % denomination !== 0n) throw new RangeError("the stake is not a whole number of credits");
  return bet / denomination;
}

/** Dice's opening as the verifier reads it: the selection the bet sent (mode + target, or mode + lo/hi, hundredths). */
function openingOf(selection: PfRoundEvidence["attempt"]["selection"]): Record<string, unknown> {
  const s = selection ?? {};
  const num = (v: unknown) => (typeof v === "number" ? v : typeof v === "string" && /^\d+$/.test(v) ? Number(v) : undefined);
  return s.mode === "under" || s.mode === "over" ? { mode: s.mode, target: num(s.target) } : { mode: s.mode, lo: num(s.lo), hi: num(s.hi) };
}

export function verifyTicketRound(evidence: PfRoundEvidence, binding: PfBindingResult): TicketCheck {
  const { mathId, pfProtocolId, gameId } = evidence.ticket;
  if (gameId !== "dice" || !DICE_V2_MATH_IDS.includes(mathId) || pfProtocolId !== PINNED_PF_PROTOCOL_ID) {
    return { kind: "other_contract", mathId, pfProtocolId };
  }
  if (binding.verdict === "mismatch") return { kind: "mismatch", failed: binding.checks.filter((c) => !c.ok).map((c) => c.check) };
  if (binding.verdict === "proof_outstanding" || !evidence.reveal) return { kind: "proof_outstanding" };
  if (evidence.end === "retired") return { kind: "retired", consistent: binding.verdict === "consistent" };

  const statement = evidence.settlement;
  const bonus = statement !== undefined && statement.bonusDeltaMinor !== "0";
  const terms = heldTerms(evidence);
  const settled = evidence.end === "settled";
  let result: VerifyPaidAmountResult;
  try {
    // Asked as a round with no amount to judge (VOID): the outcome checks all run, and `expected` carries what the rules
    // owe (with the held terms). The amount is judged below, from the player's side.
    result = verifyPaidAmount({
      game: "dice",
      held: {
        commitment: evidence.ticket.commitment,
        pfTicketId: evidence.ticket.pfTicketId,
        clientSeed: evidence.attempt.clientSeed,
        bet: creditsOf(evidence.attempt.betMinor, evidence.acceptedTerms),
        opening: openingOf(evidence.attempt.selection),
        ...(terms ? { terms } : {}),
      },
      reveal: {
        serverSeed: evidence.reveal.serverSeed,
        nonce: evidence.reveal.nonce,
        ...(evidence.reveal.clientSeed !== undefined ? { clientSeed: evidence.reveal.clientSeed } : {}),
        ...(evidence.reveal.pfTicketId !== undefined ? { pfTicketId: evidence.reveal.pfTicketId } : {}),
      },
      round: {
        status: "VOID",
        ...(evidence.detail !== undefined && evidence.detail !== null ? { detail: evidence.detail } : {}),
      },
    });
  } catch {
    // the persisted attempt is not one the contract can read (a malformed seed, a stake that is not whole credits):
    // the round cannot be shown as fair
    return { kind: "mismatch", failed: ["attempt"] };
  }
  if (result.verdict === "mismatch") return { kind: "mismatch", failed: result.checks.filter((c) => !c.ok).map((c) => c.check) };
  const expected = (result.expected ?? {}) as Record<string, unknown>;
  const failed: string[] = [];
  if (settled && expected.result === undefined) failed.push("status"); // a settled round must have played out (the words did not run out)
  if (statement !== undefined) failed.push(...statementContradictions(statement, expected, evidence.acceptedTerms, bonus));
  const amount: AmountJudgement = settled ? judgeAmount(evidence.winAmountMinor, expected, terms, bonus ? undefined : statement) : { amount: "not_applicable", failed: [] };
  failed.push(...amount.failed);
  if (failed.length > 0) return { kind: "mismatch", failed };
  if (result.verdict === "ordering_evidence_lost" || binding.verdict === "ordering_evidence_lost") return { kind: "ordering_evidence_lost" };
  if (result.verdict === "proof_outstanding") return { kind: "proof_outstanding" };
  return { kind: "verified", amount: amount.amount, result, ...(amount.because ? { unverifiedBecause: amount.because } : {}) };
}

/** The credited amount against what the rules owe under the held terms (expected.credited: the paid amount with the held
 *  ceiling applied). Without held terms nothing can be owed: unverified. With no bonus the statement's cap reduction must
 *  be what the held ceiling took off (paid − owed). */
interface AmountJudgement {
  readonly amount: "verified" | "unverified" | "not_applicable";
  readonly because?: "no_terms" | "more_than_owed";
  readonly failed: readonly string[];
}

function judgeAmount(
  credited: number | undefined,
  e: Record<string, unknown>,
  terms: PfHeldTerms | undefined,
  plainStatement: PfRoundEvidence["settlement"],
): AmountJudgement {
  if (!terms || typeof e.credited !== "string" || typeof e.paid !== "string") return { amount: "unverified", because: "no_terms", failed: [] };
  const owed = BigInt(e.credited);
  const failed: string[] = [];
  if (plainStatement !== undefined && plainStatement.capReductionMinor !== (BigInt(e.paid) - owed).toString()) failed.push("settlement_cap_reduction");
  if (credited === undefined || !Number.isSafeInteger(credited)) return { amount: "unverified", because: "no_terms", failed: [...failed, "amount"] };
  const paid = BigInt(credited);
  if (paid < owed) return { amount: "unverified", failed: [...failed, "amount"] }; // paid LESS than the rules owe
  if (paid > owed) return { amount: "unverified", because: "more_than_owed", failed }; // more than the game pays: outside the contract
  return { amount: "verified", failed };
}

/** What the server's settlement statement says that the recomputation does not. A bonus adds to the entitlement's
 *  numerator (its denominator — Dice's PPM — stays), so with a bonus the entitlement itself is the server's word and is not
 *  compared; the draw J, its exhaustion and the protocol's constants are compared always. The denomination is compared
 *  when the device held one; the entitlement only then too (the stake's credits depend on it). */
function statementContradictions(
  st: NonNullable<PfRoundEvidence["settlement"]>,
  e: Record<string, unknown>,
  held: PfRoundEvidence["acceptedTerms"],
  bonus: boolean,
): string[] {
  const failed: string[] = [];
  if (st.roundingNonce !== 0) failed.push("settlement_rounding_nonce");
  if (st.roundingLine !== 0) failed.push("settlement_rounding_line");
  if (held && st.denominationMinor !== held.denominationMinor) failed.push("settlement_denomination");
  if (typeof e.entitlementNum !== "string") {
    // the outcome is a loss: never priced, unless a bonus was (its draw is then the server's alone)
    if (e.result !== undefined && !bonus) failed.push("settlement_on_a_loss");
    return failed;
  }
  if (!bonus && held && (st.entitlementNum !== e.entitlementNum || st.entitlementDen !== e.entitlementDen)) failed.push("settlement_entitlement");
  if (st.entitlementDen !== e.entitlementDen) {
    if (!failed.includes("settlement_entitlement")) failed.push("settlement_entitlement"); // the draw's modulus
    return failed;
  }
  if (st.roundingExhausted !== e.exhausted) failed.push("settlement_rounding_exhausted");
  else if (e.exhausted === true) {
    if (st.roundingPolicy !== "floor" || st.roundingDraw !== undefined) failed.push("settlement_rounding_draw");
  } else {
    const policy = held?.settlementPolicy ?? st.roundingPolicy;
    if (st.roundingPolicy !== policy || (policy === "stochastic" && st.roundingDraw !== e.j)) failed.push("settlement_rounding_draw");
  }
  return failed;
}
